/**
 * Module Federation bootstrap shim.
 * ────────────────────────────────────────────────────────────────────────
 * This file MUST stay tiny. Its sole purpose is to defer every
 * `@walmart-dataventures/integrated-components` (and other shared-singleton)
 * import out of the synchronous module-evaluation phase so that Module
 * Federation has a chance to negotiate which copy of each singleton to use
 * at runtime — see `remote-subapps.js` for the shared-deps config and
 * `README.md` for the full version policy.
 *
 * Why a shim?
 * -----------
 * In `remote-subapps.js` we run `@walmart-dataventures/integrated-components`
 * as `singleton: true` + `eager: false`. `eager: false` is what lets MF pick
 * the highest IC version any chunk offers (shell or remote) instead of
 * locking everyone to whatever the shell happened to ship with — that's how
 * we let 21+ sub-app teams move on independent timelines without forcing
 * shell-wide lockstep upgrades.
 *
 * The catch: when a shared dep is `eager: false`, any code that imports it
 * synchronously at module top level throws
 *   "Shared module is not available for eager consumption"
 * because MF hasn't finished its negotiation handshake yet.
 *
 * The fix is the canonical MF "async boundary" pattern: keep the framework
 * entry tiny and synchronous, and dynamically import the bundle that
 * actually pulls in shared deps. By the time the dynamic chunk resolves,
 * MF has wired up the singleton table and `import "ic"` resolves correctly.
 *
 * What's allowed in this file:
 *   ✅ react, @aurora/federation — nothing that transitively pulls shared-components
 *      or integrated-components (both eager: false).
 *   ❌ @walmart-dataventures/shared-components (direct or transitive) — that
 *      includes ShellLoader which uses <Loader> from shared-components.
 *   ❌ anything from @walmart-dataventures/integrated-components (direct or
 *      transitive) — that's what the bootstrap chunk is for.
 *
 * ShellLoader is lazy-loaded (React.lazy) so it crosses its own async boundary
 * before importing shared-components. By the time the ShellLoader chunk resolves,
 * MF negotiation is complete and shared-components is safely available.
 * A nested <Suspense fallback={null}> wraps it so the outer Suspense boundary
 * is never blocked by the ShellLoader chunk fetch.
 *
 * SSR: `entry.server.tsx` imports the bootstrap module DIRECTLY (bypassing
 * this shim) because server-side rendering runs in Node and doesn't need
 * MF runtime negotiation — every package is just a real package on disk.
 */
import React, { Suspense } from "react";
import { loadSubApp } from "@aurora/federation";

// One promise, shared by both lazy wrappers, so the bootstrap chunk is
// only fetched once even when both Component + StartComponent are used.
const bootstrapPromise = import(
  /* webpackChunkName: "shell-bootstrap" */
  "./entry.client.bootstrap"
);

const LazyComponent = React.lazy(() => bootstrapPromise.then(mod => ({ default: mod.default.Component })));

const LazyStartComponent = React.lazy(() =>
  bootstrapPromise.then(mod => ({ default: mod.default.StartComponent }))
);

// ShellLoader is lazy-loaded so shared-components is only imported after
// MF negotiation completes (dynamic import creates its own async boundary).
// The nested <Suspense fallback={null}> handles the brief ShellLoader chunk
// fetch without blocking the outer Suspense boundary.
const LazyShellLoader = React.lazy(() => import("@/components/ShellLoader"));

const fallback = (
  <Suspense fallback={null}>
    <LazyShellLoader showImage />
  </Suspense>
);

export default loadSubApp({
  name: "@data-ventures-shell",
  Component: props => (
    <Suspense fallback={fallback}>
      <LazyComponent {...props} />
    </Suspense>
  ),
  StartComponent: props => (
    <Suspense fallback={fallback}>
      <LazyStartComponent {...props} />
    </Suspense>
  ),
});
